import { useEffect, useId, useMemo } from 'react';

import { TeamAvatar, TeamUserAvatar } from '@poseidon/components/Avatar';
import { Button } from '@poseidon/components/Input2';
import FilterPopover from '@poseidon/components/Input2/FilterPopover';
import { Communication, Files, Finance, General, Maps, Users } from '@poseidon/components/StrokeIcons';
import { DEFAULT_SIGNAL_BUCKETS } from '@poseidon/shared-utils/signalDefaults';
import type { FilterPickerProps } from './types';

export type PickerTag = { id: string; name: string; contactOnly: boolean };
export type PickerTeamUser = {
  userId: string;
  userEmail: string;
  userTeamName: string | null;
  profilePicture: string | null;
};
export type PickerTemplate = { id: string; name: string; userId: string };
export type PickerStage = { id: string; name: string; color?: string | null; order?: number | null };

type TagFilterPickerProps = FilterPickerProps & {
  tags: PickerTag[];
  contactOnly?: boolean;
};

export function TagFilterPicker({ tags, size, onClose, onChange, contactOnly }: TagFilterPickerProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;
  const options = tags
    .filter((tag) => !contactOnly || tag.contactOnly)
    .map(({ id, name }) => ({ value: id, label: name }));

  useEffect(() => {
    document.getElementById(popoverId)?.showPopover();
  }, [popoverId]);

  const buttonSize = size;

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        icon={<Finance.Tag01 />}
        size="sm"
        tooltip="Select a tag"
      />
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={buttonSize}
        items={options}
        onSelect={(value) => onChange(value)}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      />
    </>
  );
}

type UserFilterPickerProps = FilterPickerProps & {
  teamUsers: PickerTeamUser[];
};

export function UserFilterPicker({
  teamUsers,
  teamName,
  userId,
  teamLogo,
  size,
  onClose,
  onChange,
}: UserFilterPickerProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  const options = useMemo(() => {
    const users = teamUsers
      .slice()
      .sort((a, b) => {
        if (a.userId === userId) return -1;
        if (b.userId === userId) return 1;
        return (a.userTeamName ?? a.userEmail).localeCompare(b.userTeamName ?? b.userEmail);
      })
      .map((teamUser) => ({
        value: teamUser.userId,
        label: teamUser.userTeamName ?? teamUser.userEmail,
        icon: <TeamUserAvatar userTeam={teamUser} className="size-4 text-3xs" shape="circle" />,
        description: teamUser.userId === userId ? '(Me)' : undefined,
      }));

    return [
      {
        value: 'team',
        label: 'Team',
        icon: <TeamAvatar logo={teamLogo} name={teamName} className="size-4" shape="circle" />,
      },
      ...users,
    ];
  }, [teamUsers, userId, teamLogo, teamName]);

  useEffect(() => {
    document.getElementById(popoverId)?.showPopover();
  }, [popoverId]);

  const buttonSize = size;

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        icon={<Users.User02 />}
        size="sm"
        tooltip="Select a user"
      />
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={buttonSize}
        items={options}
        onSelect={(value) => onChange(value)}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      />
    </>
  );
}

type ConnectedUserFilterPickerProps = FilterPickerProps & {
  teamUsers: PickerTeamUser[];
};

export function ConnectedUserFilterPicker({
  teamUsers,
  userId,
  size,
  onClose,
  onChange,
}: ConnectedUserFilterPickerProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  const users = teamUsers
    .slice()
    .sort((teamUser) => (teamUser.userId === userId ? -1 : 1))
    .map((teamUser) => ({
      value: teamUser.userId,
      label: teamUser.userTeamName ?? teamUser.userEmail,
      icon: <TeamUserAvatar userTeam={teamUser} className="size-4 text-3xs" shape="circle" />,
      description: teamUser.userId === userId ? '(Me)' : undefined,
    }));

  useEffect(() => {
    document.getElementById(popoverId)?.showPopover();
  }, [popoverId]);

  const buttonSize = size;

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        icon={<Users.UserPlus01 />}
        size="sm"
        tooltip="Select a connected user"
      />
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={buttonSize}
        items={users}
        onSelect={(value) => onChange(value)}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      />
    </>
  );
}

type TemplateFilterPickerProps = FilterPickerProps & {
  templates: PickerTemplate[];
};

export function TemplateFilterPicker({ templates, userId, size, onClose, onChange }: TemplateFilterPickerProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  const items = templates.map((template) => ({
    value: template.id,
    label: template.name,
    description: template.userId === userId ? '(Mine)' : undefined,
  }));

  const sortedTemplates = items.sort((template) => (template.description != null ? -1 : 1));

  useEffect(() => {
    document.getElementById(popoverId)?.showPopover();
  }, [popoverId]);

  const buttonSize = size;

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        icon={<Maps.Rocket />}
        size="sm"
        tooltip="Select a template"
      />
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={buttonSize}
        items={sortedTemplates}
        onSelect={(value) => onChange(value)}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      />
    </>
  );
}

type StageFilterPickerProps = FilterPickerProps & {
  stages: PickerStage[];
};

export function StageFilterPicker({ stages, size, onClose, onChange }: StageFilterPickerProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  const items = stages.map((stage) => ({
    value: stage.id,
    label: stage.name,
    icon: (
      <div
        className="size-4 rounded-full shrink-0 flex items-center justify-center text-white font-semibold"
        style={{ backgroundColor: stage.color ?? undefined, fontSize: '9px' }}>
        {stage.order ?? '?'}
      </div>
    ),
  }));

  useEffect(() => {
    document.getElementById(popoverId)?.showPopover();
  }, [popoverId]);

  const buttonSize = size;

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        icon={<Files.StickerCircle />}
        size="sm"
        tooltip="Select a stage"
      />
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={buttonSize}
        items={items}
        onSelect={(value) => onChange(value)}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        filterable={false}
      />
    </>
  );
}

type CompanyFilterPickerProps = FilterPickerProps & {
  orgOptions?: { value: string; label: string }[];
};

type SignalFilterPickerProps = {
  size?: FilterPickerProps['size'];
  onClose: () => void;
  onChange: (value: string) => void;
};

export function SignalFilterPicker({ size, onClose, onChange }: SignalFilterPickerProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;

  const items = DEFAULT_SIGNAL_BUCKETS.map((b) => ({
    value: b.key,
    label: b.name,
    icon: <Communication.Rss01 className="size-4 text-mantis-500" />,
  }));

  useEffect(() => {
    document.getElementById(popoverId)?.showPopover();
  }, [popoverId]);

  const buttonSize = size;

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        icon={<Communication.Rss01 />}
        size="sm"
        tooltip="Select a signal"
      />
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={buttonSize}
        items={items}
        onSelect={(value) => onChange(value)}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        filterable={items.length > 5}
      />
    </>
  );
}

export function CompanyFilterPicker({ size, onClose, onChange, orgOptions }: CompanyFilterPickerProps) {
  const popoverId = useId();
  const anchorName = `--anchor-${popoverId.replace(/:/g, '-')}`;
  const options = orgOptions ?? [];

  useEffect(() => {
    document.getElementById(popoverId)?.showPopover();
  }, [popoverId]);

  const buttonSize = size;

  return (
    <>
      <Button
        popoverTarget={popoverId}
        style={{ anchorName } as React.CSSProperties}
        text
        icon={<General.Building07 />}
        size="sm"
        tooltip="Select a company"
      />
      <FilterPopover
        popoverId={popoverId}
        anchorName={anchorName}
        size={buttonSize}
        items={options}
        onSelect={(value) => onChange(value)}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
      />
    </>
  );
}
